import React, { useState, useRef } from 'react';
import AsyncSelect from 'react-select/async';
import AsyncDebounce from '../../utils/AsyncDebounce.js';
import { searchAppDetails } from '../../api/index.js';

export interface AppsFieldProps {
  name: string;
  value: string[];
  className?: string;
  placeholder?: string;
  isClearable?: boolean;
  isMulti?: boolean;
  disabled?: boolean;
  onAdd?: (app: any) => void;
}

const AppsField = ({
  value,
  className = '',
  disabled = false,
  isClearable = false,
  isMulti = false,
  placeholder,
  onAdd,
}: AppsFieldProps) => {
  const [inputValue, setInputValue] = useState('');
  const [defaultOptions, setDefaultOptions] = useState<any[]>([]);

  // Wrapped in useRef to avoid recreation which causes incorrect behaviour
  const loadApps = useRef(
    AsyncDebounce(async (input: string) => {
      if (!input || input.length < 3) return [];
      try {
        const result: any[] = await searchAppDetails(input);
        setDefaultOptions(result);
        return result;
      } catch (err) {
        return [];
      }
    }, 500)
  ).current;

  const onInputChange = (query, { action }) => {
    // Prevents resetting the input after option has been selected
    if (action !== 'set-value') {
      setInputValue(query);
    }
  };

  const formatLabelOption = (app) => {
    let { name: appName, os: appOs } = app;

    if (typeof app === 'string') {
      const [_name, _os] = app.split('__');
      appName = _name;
      appOs = _os;
    }

    const appLabel = `${appName} (${appOs})`;

    return (
      <div>
        <span>{appLabel}</span>
      </div>
    );
  };

  const render = () => {
    const handleChange = (option: any) => {
      if (onAdd) {
        onAdd(option);
      }
    };

    return (
      <AsyncSelect
        className={`${className}`}
        placeholder={placeholder}
        value={''}
        inputValue={inputValue}
        onInputChange={onInputChange}
        isDisabled={disabled}
        // @ts-ignore
        loadOptions={loadApps}
        getOptionValue={(o: any) => `${o.id || o.bundle}__${o.os}`}
        formatOptionLabel={formatLabelOption}
        defaultOptions={defaultOptions || []}
        onChange={handleChange}
        filterOption={(option: any) => !value.includes(`${option.data.id || option.data.bundle}__${option.data.os}`)}
        blurInputOnSelect={false}
        closeMenuOnSelect={false}
        isClearable={isClearable}
        isMulti={isMulti}
        styles={{
          control: (base, state) => ({
            ...base,
            background: 'var(--bg2)',
            border: `1px solid ${state.isFocused ? 'var(--gold)' : 'var(--bm)'}`,
            borderRadius: 4,
            boxShadow: 'none',
            minHeight: 28,
            cursor: 'text',
            '&:hover': { borderColor: 'var(--gold)' },
          }),
          menu: (base) => ({
            ...base,
            background: '#13161e',
            border: '1px solid #2a2f3d',
            borderRadius: 4,
            boxShadow: '0 4px 16px rgba(0,0,0,.6)',
            zIndex: 9999,
          }),
          menuList: (base) => ({
            ...base,
            background: '#13161e',
            borderRadius: 4,
            padding: 0,
          }),
          option: (base, state) => ({
            ...base,
            background: state.isFocused ? 'rgba(212,175,55,.12)' : 'transparent',
            color: state.isFocused ? 'var(--gold)' : 'var(--t2)',
            fontFamily: 'var(--mono)',
            fontSize: 11,
            padding: '6px 10px',
            cursor: 'pointer',
          }),
          input: (base) => ({
            ...base,
            color: 'var(--t1)',
            fontFamily: 'var(--mono)',
            fontSize: 11,
            margin: 0,
            padding: 0,
          }),
          placeholder: (base) => ({
            ...base,
            color: 'var(--t3)',
            fontFamily: 'var(--mono)',
            fontSize: 11,
          }),
          singleValue: (base) => ({
            ...base,
            color: 'var(--t1)',
            fontFamily: 'var(--mono)',
            fontSize: 11,
          }),
          valueContainer: (base) => ({
            ...base,
            padding: '2px 8px',
          }),
          dropdownIndicator: (base) => ({
            ...base,
            color: 'var(--t3)',
            padding: '0 6px',
            '&:hover': { color: 'var(--gold)' },
          }),
          clearIndicator: (base) => ({
            ...base,
            color: 'var(--t3)',
            padding: '0 4px',
            '&:hover': { color: 'var(--red)' },
          }),
          indicatorSeparator: () => ({ display: 'none' }),
          noOptionsMessage: (base) => ({
            ...base,
            color: 'var(--t3)',
            fontFamily: 'var(--mono)',
            fontSize: 11,
          }),
          loadingMessage: (base) => ({
            ...base,
            color: 'var(--t3)',
            fontFamily: 'var(--mono)',
            fontSize: 11,
          }),
        }}
      />
    );
  };
  return render();
};

export default AppsField;
